import {CommonModule} from '@angular/common';
import {Component, computed, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Dialog} from '@angular/cdk/dialog';
import {CampanaSelectComponent, Option} from '../../../campaña/components/campana-select/campana-select-component';
import {ClienteSelectComponent} from "../../../campaña/components/cliente-select/cliente-select-component";
import {UserRole, UserRoleStore} from '@/app/core/states/user-role-stare/client-role-state.store';
import {CampaignStoreService} from '../../../campaña/services/campaign-store.service';
import {ClientsService} from '@/app/features/clientes/services/clients.service';
import {ReporteFiltrosDialogHost} from '../filters-report/components/modal-filtros/reporte-filtros-dialog.host';
import {AnalysisDemographicFilters} from '../../infrastructure/analysis.api.types';
import {DemographicKey} from '../../services/overview-demographics.service';
import {AnalysisMode} from '../filters-report/reporte-filtros.component';
import {createScenarioLabel} from '../../utils/report-chart.utils';
import {NavigationStart, Router} from '@angular/router';
import {ClientStore} from "@/app/core/states/clientes-state/client-state.store";
import {filter} from 'rxjs';
import {ThemeService} from '@/app/core/services/theme/theme.service';

type DialogResult =
  | { action: 'cancel' }
  | { action: 'reset' }
  | {
  action: 'apply';
  facets: DemographicKey[];
  filters: Partial<AnalysisDemographicFilters>;
  analysisMode: AnalysisMode;
};

export type Scenario = {
  id: string;
  facets: DemographicKey[];
  filters: Partial<AnalysisDemographicFilters>;
  label: string;
  analysisMode: AnalysisMode;
};

@Component({
  selector: 'app-report-controls',
  standalone: true,
  imports: [
    CommonModule,
    ClienteSelectComponent,
    CampanaSelectComponent
  ],
  templateUrl: './report-controls.component.html',
  styleUrls: ['./report-controls.component.scss']
})
export class ReportControlsComponent implements OnInit, OnDestroy {
  @Input() guideId!: 'guia-i' | 'guia-ii' | 'guia-iii';
  @Input() hasData: boolean = false;
  @Input() disableCategories: boolean = false;
  @Input() isExporting: boolean = false;
  @Output() scenariosChange = new EventEmitter<Scenario[]>();
  @Output() hasAppliedFiltersChange = new EventEmitter<boolean>();
  @Output() analysisModeChange = new EventEmitter<AnalysisMode>();
  @Output() activeFacetsChange = new EventEmitter<DemographicKey[]>();
  @Output() activeFiltersChange = new EventEmitter<Partial<AnalysisDemographicFilters>>();
  @Output() exportPdf = new EventEmitter<void>();
  isAdmin: boolean = false;
  clientOptions: Option[] = [];
  selectedClientOption?: Option;
  campaignOptions: Option[] = [];
  selectedCampaignOption?: Option;
  clients: any[] = [];
  private userState = inject(UserRoleStore);
  private store = inject(CampaignStoreService);
  private clientsService = inject(ClientsService);
  private clientStore = inject(ClientStore);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(Dialog);
  private router = inject(Router);
  private navigationSub: any;

  protected reportContent = computed(() => this.themeContent()?.reports);
  protected reportControlsComponent = computed(() => this.reportContent()?.reportControlsComponent)
  private themeService = inject(ThemeService);
  protected themeContent = this.themeService.themeContent;

  ngOnInit(): void {
    const user: UserRole | null = this.userState.user();
    if (user === null) {
      return;
    }

    this.navigationSub = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const previousUrl = this.router.url; // la ruta actual antes del cambio
        const nextUrl = event.url;           // hacia dónde se navega

        // ⚠️ si vienes DESDE clientes y vas a reportes, limpia el store
        if (previousUrl.startsWith('/clientes') && nextUrl.startsWith('/reportes')) {
          this.clientStore.resetData();
          this.store.clearCampaigns();
        }
      });

    if (user.group === 'USER') {
      const clientPublicId = user.attributes?.['publicId'] || user.id;
      const clientName =
        user.attributes?.['clientName'] || user.username || 'Usuario';

      this.clientOptions = [{value: clientPublicId, label: clientName}];
      this.store.loadCampaigns(user.id ?? '');
    } else {
      this.isAdmin = true;
      this.clientsService
        .getAllClients()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(clients => {
          this.clients = clients;
          this.clientOptions = clients.map(client => ({
            value: client.publicId,
            label: client.name,
          })).sort((a, b) => a.label.localeCompare(b.label));

          // Si hay un cliente en el store, pre-seleccionarlo
          const storedClient = this.clientStore.client();
          if (storedClient) {
            const clientOption = this.clientOptions.find(opt => opt.value === storedClient.publicId);
            if (clientOption) {
              this.selectedClientOption = clientOption;
              this.store.loadCampaignsForClient(storedClient.publicId);
            }
          }
        });
    }

    this.store.campaignsOptions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(opts => (this.campaignOptions = [...opts].sort((a, b) => a.label.localeCompare(b.label))));

    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(opt => (this.selectedCampaignOption = opt ?? undefined));
  }

  ngOnDestroy() {
    if (!this.isAdmin) {
      return;
    }

    if (!this.router.url.startsWith('/reportes')) {
      this.store.clearCampaigns();
      this.clientStore.resetData();
    }
  }

  onClientSelect(opt: Option) {
    // Sincronizar el store ANTES de cualquier otra cosa
    const clientToSet = this.clients.find(c => c.publicId === opt.value);
    if (clientToSet) {
      this.clientStore.setClient(clientToSet);
    }

    this.selectedClientOption = opt;
    this.selectedCampaignOption = undefined;
    this.campaignOptions = [];
    this.store.setSelectedCampaign(null);

    if (this.guideId !== 'guia-i') {
      // Si no estamos en guia-1, solo navegamos. El ngOnInit de la nueva página se encargará de cargar las campañas.
      this.router.navigate(['/reportes/guia-1']);
    } else {
      // Si ya estamos en guia-1, no navegamos, así que cargamos las campañas manualmente.
      this.store.loadCampaignsForClient(opt.value);
    }
  }

  onCampaignSelect(opt: Option) {
    this.store.setSelectedCampaign(opt);

    if (this.guideId !== 'guia-i') {
      this.router.navigate(['/reportes/guia-1']);
    }
  }

  openFiltros() {

    const ref = this.dialog.open<DialogResult>(ReporteFiltrosDialogHost, {
      panelClass: ['report-dialog'],
      backdropClass: 'report-backdrop',
      width: '1400px',
      maxWidth: '98vw',
      disableClose: true,
      data: { disableCategories: this.disableCategories }
    });

    ref.closed.subscribe(res => {
      if (!res || res.action === 'cancel') return;

      if (res.action === 'reset') {
        this.scenariosChange.emit([]);
        this.hasAppliedFiltersChange.emit(false);
        this.analysisModeChange.emit('final-assessments');
        this.activeFacetsChange.emit([]);
        this.activeFiltersChange.emit({});
        return;
      }

      if (res.action === 'apply') {
        const facets = [...(res.facets ?? [])];
        const filters = {...(res.filters ?? {})};
        const analysisMode = res.analysisMode ?? 'final-assessments';

        const label = createScenarioLabel(facets, filters, analysisMode, this.guideId);

        const newScenarios = [
          {id: crypto.randomUUID(), facets, filters, label, analysisMode},
        ];

        this.scenariosChange.emit(newScenarios);
        this.hasAppliedFiltersChange.emit(true);
        this.analysisModeChange.emit(analysisMode);
        this.activeFacetsChange.emit(facets);
        this.activeFiltersChange.emit(filters);
      }
    });
  }
}
